import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useAuth } from "../lib/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "../lib/ui";
import { Plus, RefreshCw, X, Pencil, Trash2, Check } from "lucide-react";
import AccountSwitcher from "../components/AccountSwitcher";

const ROLES = ["admin", "manager", "employee", "viewer"];
const STATUS_STYLE = { pending: "bg-amber-50 text-amber-700", accepted: "bg-emerald-50 text-emerald-700", revoked: "bg-slate-100 text-slate-500", expired: "bg-red-50 text-red-700" };

export default function AccountSettings() {
  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Account Settings" subtitle="Manage your organisation profile, members and invitations" />
      <Tabs defaultValue="profile">
        <div className="mb-4">
          <TabsList className="flex-wrap h-auto items-center gap-1">
            {["profile", "members", "invitations", "roles"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`acct-tab-${t}`} className="capitalize">{t}</TabsTrigger>
            ))}
            <AccountSwitcher />
            <TabsTrigger value="preferences" data-testid="acct-tab-preferences" className="capitalize">preferences</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="profile"><Profile /></TabsContent>
        <TabsContent value="members"><Members /></TabsContent>
        <TabsContent value="invitations"><Invitations /></TabsContent>
        <TabsContent value="roles"><CustomRoles /></TabsContent>
        <TabsContent value="preferences"><Preferences /></TabsContent>
      </Tabs>
    </div>
  );
}

function Profile() {
  const [acc, setAcc] = useState(null);
  const set = (k, v) => setAcc((p) => ({ ...p, [k]: v }));
  const setP = (k, v) => setAcc((p) => ({ ...p, profile: { ...(p.profile || {}), [k]: v } }));
  useEffect(() => { api.get("/account/me").then(({ data }) => setAcc(data)).catch(() => {}); }, []);
  if (!acc) return <div className="text-slate-400 text-sm">Loading…</div>;
  const p = acc.profile || {};
  const save = async () => {
    try {
      await api.put("/account/profile", { name: acc.name, organization_name: p.organization_name, team_name: p.team_name, team_size: p.team_size, industry: p.industry });
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="max-w-xl space-y-4 bg-white border rounded-xl p-5" data-testid="acct-profile">
      <div className="space-y-1.5"><Label>Account name</Label><Input data-testid="acct-name" value={acc.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
      <div className="flex gap-2 text-xs text-slate-500"><span className="rounded-full bg-slate-100 px-2 py-0.5">{acc.human_id}</span><span className="rounded-full bg-slate-100 px-2 py-0.5 capitalize">{acc.type}</span><span className="rounded-full bg-slate-100 px-2 py-0.5 capitalize">{(acc.status || "").replace(/_/g, " ")}</span></div>
      {acc.type === "organisation" && <div className="space-y-1.5"><Label>Organisation name</Label><Input value={p.organization_name || ""} onChange={(e) => setP("organization_name", e.target.value)} /></div>}
      {acc.type === "team" && <><div className="space-y-1.5"><Label>Team name</Label><Input value={p.team_name || ""} onChange={(e) => setP("team_name", e.target.value)} /></div><div className="space-y-1.5"><Label>Team size</Label><Input value={p.team_size || ""} onChange={(e) => setP("team_size", e.target.value)} /></div></>}
      <div className="space-y-1.5"><Label>Industry</Label><Input value={p.industry || ""} onChange={(e) => setP("industry", e.target.value)} /></div>
      <Button data-testid="acct-profile-save" onClick={save}>Save profile</Button>
    </div>
  );
}

function Members() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [roleOpts, setRoleOpts] = useState(ROLES);
  const load = () => api.get("/account/members").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/account/roles").then(({ data }) => setRoleOpts([...ROLES, ...data.map((r) => r.code)])).catch(() => {});
  }, []);
  const changeRole = async (m, role) => { try { await api.put(`/account/members/${m.id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const remove = async (m) => { if (!window.confirm(`Remove ${m.full_name}? They will lose access (history preserved).`)) return; try { await api.delete(`/account/members/${m.id}`); toast.success("Member removed"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const reactivate = async (m) => { try { await api.post(`/account/members/${m.id}/reactivate`); toast.success("Reactivated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="bg-white border rounded-xl overflow-x-auto" data-testid="acct-members">
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th></tr></thead>
        <tbody>
          {rows.map((m) => {
            const isOwner = m.account_role === "owner";
            return (
              <tr key={m.id} className="border-b last:border-0" data-testid={`member-row-${m.email}`}>
                <td className="px-4 py-2.5 font-medium">{m.full_name}{isOwner && <span className="ml-2 text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">Owner</span>}</td>
                <td className="px-4 py-2.5 text-slate-500">{m.email}</td>
                <td className="px-4 py-2.5">
                  {isOwner || m.id === user?.id ? <span className="capitalize">{m.role?.replace("_", " ")}</span> : (
                    <Select value={roleOpts.includes(m.role) ? m.role : "employee"} onValueChange={(v) => changeRole(m, v)}>
                      <SelectTrigger data-testid={`member-role-${m.email}`} className="w-32 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{roleOpts.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-4 py-2.5"><span className={`text-xs rounded-full px-2 py-0.5 ${m.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{m.active ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-2.5 text-right">
                  {!isOwner && m.id !== user?.id && (m.active
                    ? <Button variant="ghost" size="sm" className="text-red-600" data-testid={`member-remove-${m.email}`} onClick={() => remove(m)}>Remove</Button>
                    : <Button variant="ghost" size="sm" onClick={() => reactivate(m)}>Reactivate</Button>)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Invitations() {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employee");
  const [roleOpts, setRoleOpts] = useState(ROLES);
  const load = () => api.get("/account/invitations").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/account/roles").then(({ data }) => setRoleOpts([...ROLES, ...data.map((r) => r.code)])).catch(() => {});
  }, []);
  const invite = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Enter a valid email");
    try { await api.post("/account/invitations", { email, role }); toast.success("Invitation sent"); setEmail(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resend = async (i) => { try { await api.post(`/account/invitations/${i.id}/resend`); toast.success("Invitation resent"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const revoke = async (i) => { try { await api.post(`/account/invitations/${i.id}/revoke`); toast.success("Revoked"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div data-testid="acct-invitations">
      <div className="flex flex-wrap gap-2 mb-4 bg-white border rounded-xl p-4">
        <Input data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" className="w-64 h-9" />
        <Select value={role} onValueChange={setRole}><SelectTrigger data-testid="invite-role-select" className="w-36 h-9"><SelectValue /></SelectTrigger><SelectContent>{roleOpts.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
        <Button data-testid="invite-send" onClick={invite}><Plus size={16} className="mr-1" /> Send invite</Button>
      </div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Sent</th><th className="px-4 py-2.5"></th></tr></thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} className="border-b last:border-0" data-testid={`invite-row-${i.email}`}>
                <td className="px-4 py-2.5 font-medium">{i.email}</td>
                <td className="px-4 py-2.5 capitalize">{i.role}</td>
                <td className="px-4 py-2.5"><span className={`text-xs rounded-full px-2 py-0.5 capitalize ${STATUS_STYLE[i.status] || "bg-slate-100"}`}>{i.status}</span></td>
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(i.created_at)}</td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {(i.status === "pending" || i.status === "expired") && <Button variant="ghost" size="sm" data-testid={`invite-resend-${i.email}`} onClick={() => resend(i)}><RefreshCw size={14} className="mr-1" />Resend</Button>}
                  {i.status === "pending" && <Button variant="ghost" size="sm" className="text-red-600" data-testid={`invite-revoke-${i.email}`} onClick={() => revoke(i)}><X size={14} className="mr-1" />Revoke</Button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No invitations yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Preferences() {
  const [acc, setAcc] = useState(null);
  useEffect(() => { api.get("/account/me").then(({ data }) => setAcc(data)).catch(() => {}); }, []);
  if (!acc) return <div className="text-slate-400 text-sm">Loading…</div>;
  const prefs = acc.preferences || {};
  const setPref = (k, v) => setAcc((p) => ({ ...p, preferences: { ...(p.preferences || {}), [k]: v } }));
  const save = async () => {
    try { await api.put("/account/preferences", { email_notifications: prefs.email_notifications ?? true, default_landing: prefs.default_landing || "dashboard" }); toast.success("Preferences saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="max-w-xl space-y-4 bg-white border rounded-xl p-5" data-testid="acct-preferences">
      <div className="flex items-center justify-between"><div><div className="font-medium text-sm">Email notifications</div><div className="text-xs text-slate-500">Mirror in-app notifications to email for this account</div></div>
        <Switch data-testid="pref-email-notif" checked={prefs.email_notifications ?? true} onCheckedChange={(v) => setPref("email_notifications", v)} /></div>
      <div className="space-y-1.5"><Label>Default landing page</Label>
        <Select value={prefs.default_landing || "dashboard"} onValueChange={(v) => setPref("default_landing", v)}>
          <SelectTrigger data-testid="pref-landing" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{["dashboard", "tasks", "workspaces", "my-work"].map((x) => <SelectItem key={x} value={x} className="capitalize">{x.replace("-", " ")}</SelectItem>)}</SelectContent>
        </Select></div>
      <Button data-testid="pref-save" onClick={save}>Save preferences</Button>
    </div>
  );
}

function CustomRoles() {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState({ permissions: [], base_tiers: ["admin", "manager", "employee", "viewer"] });
  const [edit, setEdit] = useState(null);
  const [show, setShow] = useState(false);
  const load = () => api.get("/account/roles").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/account/permissions-catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);
  const del = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await api.delete(`/account/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="acct-roles">
      <div className="flex justify-end mb-3"><Button data-testid="role-new" onClick={() => { setEdit(null); setShow(true); }}><Plus size={16} className="mr-1" /> New Role</Button></div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Visibility tier</th><th className="px-4 py-2.5">Permissions</th><th className="px-4 py-2.5">In use</th><th className="px-4 py-2.5"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`role-row-${r.code}`}>
                <td className="px-4 py-2.5 font-medium">{r.name}<div className="text-xs text-slate-400">{r.code}</div></td>
                <td className="px-4 py-2.5 capitalize">{r.base_tier}</td>
                <td className="px-4 py-2.5 text-slate-500">{(r.permissions || []).length} permissions</td>
                <td className="px-4 py-2.5">{r.in_use}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEdit(r); setShow(true); }}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" className="text-red-600" data-testid={`role-delete-${r.code}`} onClick={() => del(r)}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No custom roles yet. Create one to tailor access for your team.</td></tr>}
          </tbody>
        </table>
      </div>
      {show && <RoleModal row={edit} catalog={catalog} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function RoleModal({ row, catalog, onClose, onSaved }) {
  const [f, setF] = useState(row || { name: "", base_tier: "employee", permissions: [] });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (perm) => setF((p) => ({ ...p, permissions: (p.permissions || []).includes(perm) ? p.permissions.filter((x) => x !== perm) : [...(p.permissions || []), perm] }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Role name required");
    try {
      if (row) await api.put(`/account/roles/${row.id}`, f);
      else await api.post("/account/roles", f);
      toast.success("Role saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="role-modal" className="max-w-lg">
        <DialogHeader><DialogTitle>{row ? "Edit" : "New"} role</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Role name</Label><Input data-testid="role-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Team Lead" /></div>
          <div className="space-y-1.5">
            <Label>Visibility tier</Label>
            <Select value={f.base_tier} onValueChange={(v) => set("base_tier", v)}>
              <SelectTrigger data-testid="role-tier"><SelectValue /></SelectTrigger>
              <SelectContent>{catalog.base_tiers.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-slate-400">Controls which records this role can see, reusing the built-in scoping.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Allowed actions</Label>
            <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto border rounded-lg p-3">
              {catalog.permissions.map((perm) => (
                <label key={perm} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid={`role-perm-${perm}`} checked={(f.permissions || []).includes(perm)} onChange={() => toggle(perm)} />
                  <span className="capitalize">{perm.replace(/_/g, " ")}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="role-save" onClick={save}><Check size={14} className="mr-1" />Save role</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

